import MessageBanner from 'LuxKit/Banners/MessageBanner'
import FormatDateTime from 'components/utils/Formatters/FormatDateTime'
import { FormattedMessage, useIntl } from 'react-intl'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { daysUntilDate } from 'lib/datetime/dateUtils'
import { getThemeClassName } from 'lib/theme/themeUtils'
import FormatLoyaltyBonusPoints from 'loyalty/components/Formatters/FormatLoyaltyBonusPoints'
import FormatLoyaltyStatusCredits from 'loyalty/components/Formatters/FormatLoyaltyStatusCredits'
import { LOYALTY_LUX_PLUS_RENEWAL_BONUS_POINTS, LOYALTY_LUX_PLUS_RENEWAL_BONUS_STATUS_CREDITS } from 'loyalty/constants/loyaltyBonusPoints'
import getIsLoyaltyLuxPlusRenewalBonusEnabled from 'loyalty/selectors/getIsLoyaltyLuxPlusRenewalBonusEnabled'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import { LUXURY_PLUS, LUXURY_PLUS_PATHS } from 'luxPlus/constants/base'
import { getIsPendingRenewalLuxPlusMember } from 'luxPlus/selectors/featureToggle'
import React, { useCallback } from 'react'
import * as Analytics from 'analytics/analytics'
import purchaseLuxPlus from 'actions/luxPlus/purchaseLuxPlus'

interface Props {
  hideManageMembership?: boolean
}

function LuxPlusRenewalMessageBanner({ hideManageMembership = false }: Props) {
  const intl = useIntl()
  const isLoyaltyLuxPlusRenewalBonusEnabled = useAppSelector(getIsLoyaltyLuxPlusRenewalBonusEnabled)
  const isPendingRenewalLuxPlusMember = useAppSelector(getIsPendingRenewalLuxPlusMember)
  const subscriptionItem = useAppSelector(state => state.auth.account.luxPlus.member.subscription.item)
  const renewalStatus = subscriptionItem?.renewalStatus
  const dispatch = useAppDispatch()

  const handleRenewal = useCallback(() => {
    Analytics.trackClientEvent({
      subject: 'manual_renewal_purchase',
      action: 'add_to_cart_lux_plus_standalone',
      category: LUXURY_PLUS.ANALYTICS_CATEGORY,
      type: 'interaction',
    })
    dispatch(purchaseLuxPlus({ hasJoinFee: false }))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (
    !subscriptionItem ||
    !isPendingRenewalLuxPlusMember ||
    renewalStatus !== 'ALLOW_RENEWAL' ||
    subscriptionItem.nextChargeAttemptDate === undefined
  ) return null

  const daysUntilNextChargeAttempt = daysUntilDate(new Date(subscriptionItem.nextChargeAttemptDate))

  const loyaltyBonus = <b>
    <FormattedMessage
      id="CiQMCUSUut-K5Z-qd0sd"
      defaultMessage="{statusCredits} & {bonusPoints}"
      description="Status credits and bonus points renewal incentive in LuxPlus renewal banner"
      values={{
        statusCredits: <FormatLoyaltyStatusCredits statusCredits={LOYALTY_LUX_PLUS_RENEWAL_BONUS_STATUS_CREDITS} />,
        bonusPoints: <FormatLoyaltyBonusPoints points={LOYALTY_LUX_PLUS_RENEWAL_BONUS_POINTS} />,
      }}
    />
  </b>
  const loyaltyProgramName = getLoyaltyProgramDisplayName()
  const programName = LUXURY_PLUS.PROGRAM_NAME

  const paymentDeclinedHeading = intl.formatMessage({
    id: 'K3Ocd8BVK_Y4_76f9MPz',
    defaultMessage: 'Your payment was declined',
    description: 'Heading for critical banner when LuxPlus+ membership payment is declined',
  })
  const renewNowLabel = intl.formatMessage({
    id: 'EJGBM0vt7TPZobzaLbHQ',
    defaultMessage: 'Renew now',
    description: 'Primary action button text to renew LuxPlus+ membership after payment decline',
  })
  const manageMembershipLabel = intl.formatMessage({
    id: 'jsHO7Q3lDL7jljrIV-AX',
    defaultMessage: 'Manage membership',
    description: 'Secondary action button text to manage LuxPlus+ membership after payment decline',
  })

  return <MessageBanner
    className={getThemeClassName({ mobile: 'default' })}
    heading={paymentDeclinedHeading}
    kind="critical"
    primaryActionButtonProps={{ children: renewNowLabel, onClick: handleRenewal }}
    secondaryActionButtonProps={hideManageMembership ? undefined : { children: manageMembershipLabel, to: `/${LUXURY_PLUS_PATHS.ACCOUNT_MANAGEMENT_PAGE}` }}
  >
    {daysUntilNextChargeAttempt === 0 && isLoyaltyLuxPlusRenewalBonusEnabled && <FormattedMessage
      id="q6VGOVdrp0aLZFwxVddE"
      defaultMessage="Your benefits are on hold. We’ll try to process the payment again on {retryDate}. Renew now to receive your {loyaltyBonus} with {loyaltyProgramName} and continue enjoying {programName} pricing, bonus inclusions, early access & more."
      description="Payment retry on date with loyalty bonus renewal prompt for LuxPlus+ members"
      values={{
        retryDate: <FormatDateTime value={subscriptionItem.nextChargeAttemptDate} format="dmy-casual" />,
        loyaltyBonus,
        loyaltyProgramName,
        programName,
      }}
    />}
    {daysUntilNextChargeAttempt === 0 && !isLoyaltyLuxPlusRenewalBonusEnabled && <FormattedMessage
      id="4QVmXfsjeGHcC5mAY9o7"
      defaultMessage="Your benefits are on hold. We’ll try to process the payment again on {retryDate}. Renew now to continue enjoying {programName} pricing, bonus inclusions, early access & more."
      description="Payment retry on date with renewal prompt for LuxPlus+ members"
      values={{
        retryDate: <FormatDateTime value={subscriptionItem.nextChargeAttemptDate} format="dmy-casual" />,
        programName,
      }}
    />}
    {daysUntilNextChargeAttempt !== 0 && isLoyaltyLuxPlusRenewalBonusEnabled && <FormattedMessage
      id="ckhhdyp0KE873d-tBjAR"
      defaultMessage="Your benefits are on hold. We’ll try to process the payment again in {count, plural, one {# day} other {# days}}. Renew now to receive your {loyaltyBonus} with {loyaltyProgramName} and continue enjoying {programName} pricing, bonus inclusions, early access & more."
      description="Payment retry in days with loyalty bonus renewal prompt for LuxPlus+ members"
      values={{
        count: daysUntilNextChargeAttempt,
        loyaltyBonus,
        loyaltyProgramName,
        programName,
      }}
    />}
    {daysUntilNextChargeAttempt !== 0 && !isLoyaltyLuxPlusRenewalBonusEnabled && <FormattedMessage
      id="HlO_bB4FaccqexlxH1HY"
      defaultMessage="Your benefits are on hold. We’ll try to process the payment again in {count, plural, one {# day} other {# days}}. Renew now to continue enjoying {programName} pricing, bonus inclusions, early access & more."
      description="Payment retry in days with renewal prompt for LuxPlus+ members"
      values={{
        count: daysUntilNextChargeAttempt,
        programName,
      }}
    />}
  </MessageBanner>
}

export default LuxPlusRenewalMessageBanner
